import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, ShieldCheckIcon, BarChart3Icon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { cryptocurrencies, exchanges } from '../data/mockData';
export function HomePage() {
  const topCryptos = cryptocurrencies.slice(0, 5);
  const topExchanges = exchanges.slice(0, 5);
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-id="element-384">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20" data-id="element-385">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-386">
          <div className="text-center max-w-3xl mx-auto" data-id="element-387">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6" data-id="element-388">
              <ShieldCheckIcon size={18} data-id="element-389" />
              <span className="text-sm font-medium" data-id="element-390">
                Trusted Crypto Intelligence
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-391">
              The TrustPilot for{' '}
              <span className="text-blue-400" data-id="element-392">Cryptocurrency</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-393">
              Comprehensive reviews, trust scores, and intelligence for
              cryptocurrencies and exchanges. Make informed decisions with
              verified data.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-394">
              <Link to="/cryptocurrencies" data-id="element-395">
                <Button size="lg" className="shadow-lg shadow-blue-500/20" data-id="element-396">
                  Explore Cryptocurrencies
                </Button>
              </Link>
              <Link to="/exchanges" data-id="element-397">
                <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-id="element-398">
                  View Exchanges
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-yellow-50 border-y border-yellow-200" data-id="element-399">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-400">
          <div className="flex items-start gap-3" data-id="element-401">
            <div className="flex-shrink-0 mt-0.5" data-id="element-402">
              <div className="w-1 h-full bg-yellow-500 rounded-full" data-id="element-403"></div>
            </div>
            <div className="flex-1" data-id="element-404">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-405">
                <strong className="font-semibold text-gray-900" data-id="element-406">
                  Disclaimer:
                </strong>{' '}
                This website aggregates crypto-related information from various
                public sources. The content is provided for informational
                purposes only and does not constitute financial or investment
                advice. Always conduct independent research before making
                financial decisions.{' '}
                <Link to="/terms-of-use" className="text-blue-600 hover:text-blue-700 font-semibold underline" data-id="element-407">
                  Read full Terms
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-408">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-409">
          <Card className="p-6 text-center" hover data-id="element-410">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-411">
              <ShieldCheckIcon className="text-blue-600" size={24} data-id="element-412" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-413">Trust Scores</h3>
            <p className="text-slate-600" data-id="element-414">
              Proprietary trust scoring system based on security, transparency,
              and user reviews.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-415">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-416">
              <BarChart3Icon className="text-green-600" size={24} data-id="element-417" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-418">Market Data</h3>
            <p className="text-slate-600" data-id="element-419">
              Real-time pricing, market cap, volume, and performance metrics for
              informed decisions.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-420">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-421">
              <TrendingUpIcon className="text-purple-600" size={24} data-id="element-422" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-423">Verified Reviews</h3>
            <p className="text-slate-600" data-id="element-424">
              Authentic user reviews from verified traders and investors in the
              crypto space.
            </p>
          </Card>
        </div>
      </section>

      {/* Top Cryptocurrencies Preview */}
      <section className="py-16 bg-slate-50" data-id="element-425">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-426">
          <div className="flex items-center justify-between mb-8" data-id="element-427">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-428">
              Top Cryptocurrencies
            </h2>
            <Link to="/cryptocurrencies" data-id="element-429">
              <Button variant="outline" data-id="element-430">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4" data-id="element-431">
            {topCryptos.map(crypto => <Link key={crypto.id} to={`/cryptocurrency/${crypto.id}`} data-id="element-432">
                <Card className="p-5" hover data-id="element-433">
                  <div className="flex items-center justify-between" data-id="element-434">
                    <div className="flex items-center gap-4" data-id="element-435">
                      <div className="flex items-center gap-3" data-id="element-436">
                        <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-437">
                          {crypto.rank}
                        </span>
                        <img src={crypto.logo} alt={crypto.name} className="w-10 h-10" data-id="element-438" />
                      </div>
                      <div data-id="element-439">
                        <div className="flex items-center gap-2 mb-1" data-id="element-440">
                          <h3 className="font-semibold text-lg" data-id="element-441">
                            {crypto.name}
                          </h3>
                          <span className="text-slate-500 text-sm" data-id="element-442">
                            {crypto.symbol}
                          </span>
                        </div>
                        <TrustScore score={crypto.trustScore} size="sm" data-id="element-443" />
                      </div>
                    </div>

                    <div className="text-right" data-id="element-444">
                      <div className="font-semibold text-lg mb-1" data-id="element-445">
                        ${crypto.price.toLocaleString()}
                      </div>
                      <div className={`text-sm font-medium ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-446">
                        {crypto.change24h >= 0 ? '+' : ''}
                        {crypto.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Top Exchanges Preview */}
      <section className="py-16" data-id="element-447">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-448">
          <div className="flex items-center justify-between mb-8" data-id="element-449">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-450">Top Exchanges</h2>
            <Link to="/exchanges" data-id="element-451">
              <Button variant="outline" data-id="element-452">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-453">
            {topExchanges.map(exchange => <Link key={exchange.id} to={`/exchange/${exchange.id}`} data-id="element-454">
                <Card className="p-5" hover data-id="element-455">
                  <div className="flex items-center gap-3 mb-4" data-id="element-456">
                    <div className="flex items-center gap-2" data-id="element-457">
                      <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-458">
                        {exchange.rank}
                      </span>
                      <img src={exchange.logo} alt={exchange.name} className="w-10 h-10" data-id="element-459" />
                    </div>
                    <div data-id="element-460">
                      <h3 className="font-semibold text-lg" data-id="element-461">{exchange.name}</h3>
                      <TrustScore score={exchange.trustScore} size="sm" data-id="element-462" />
                    </div>
                  </div>

                  <div className="space-y-2 text-sm" data-id="element-463">
                    <div className="flex justify-between" data-id="element-464">
                      <span className="text-slate-600" data-id="element-465">24h Volume:</span>
                      <span className="font-medium" data-id="element-466">
                        ${(exchange.tradingVolume / 1000000000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-467">
                      <span className="text-slate-600" data-id="element-468">Coins:</span>
                      <span className="font-medium" data-id="element-469">
                        {exchange.supportedCoins}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-470">
                      <span className="text-slate-600" data-id="element-471">Trading Fee:</span>
                      <span className="font-medium" data-id="element-472">{exchange.tradingFee}</span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white" data-id="element-473">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-474">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-475">
            Ready to Make Informed Decisions?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-476">
            Join thousands of investors who trust CryptoTopReviews for their
            crypto intelligence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-477">
            <Link to="/cryptocurrencies" data-id="element-478">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="element-479">
                Start Exploring
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}